import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { ToastService, type Toast, type ToastKind } from '../../core/toast/toast.service';
import { Icon } from '../icon/icon';
import type { IconName } from '../icon/icon-registry';

// Mapa a nivel de módulo (no class field con identificador importado pelado:
// quedaría undefined bajo el transform SSR de Vite — ver comentarios en features/*).
const KIND_ICONS: Record<ToastKind, IconName> = {
  success: 'circle-check',
  info: 'info',
  warning: 'triangle-alert',
  error: 'circle-x',
};

// warning/error interrumpen (assertive); success/info solo informan (polite).
const KIND_ROLES: Record<ToastKind, 'status' | 'alert'> = {
  success: 'status',
  info: 'status',
  warning: 'alert',
  error: 'alert',
};

@Component({
  selector: 'app-toast-container',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon],
  template: `
    <div class="toast-container" role="region" aria-label="Notificaciones">
      @for (toast of toasts(); track toast.id) {
        <div
          class="toast"
          [class]="'toast--' + toast.kind"
          [attr.role]="roleFor(toast.kind)"
          animate.leave="toast--leaving"
        >
          <app-icon class="toast__icon" [name]="iconFor(toast.kind)" [size]="18" />
          <p class="toast__message">{{ toast.message }}</p>
          @if (toast.action; as action) {
            <button type="button" class="toast__action" (click)="runAction(toast)">
              {{ action.label }}
            </button>
          }
          <button
            type="button"
            class="toast__close"
            aria-label="Cerrar notificación"
            (click)="dismiss(toast.id)"
          >
            <app-icon name="x" [size]="16" />
          </button>
        </div>
      }
    </div>
  `,
  styles: [
    `
      .toast-container {
        position: fixed;
        right: var(--space-6);
        bottom: var(--space-6);
        z-index: var(--z-toast);
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
        width: min(380px, calc(100vw - 2 * var(--space-6)));
        /* La región no bloquea la página; solo los toasts capturan interacción. */
        pointer-events: none;
      }

      /* QA AD15 C2 (PFA QA D14 R5): the toasts sit above the table's open "Vista" menu (z-toast >
         z-drawer) and covered part of it. While that menu is open they step aside; they come back,
         as they were, when it closes (an error toast stays until dismissed, as always). */
      :host-context(body:has(.tt__vista-menu)) .toast-container {
        visibility: hidden;
      }

      /* Semántica por ícono en color pleno del token de estado (sin chip entintado de fondo),
         con borde hairline uniforme — nada de franja lateral de acento. */
      .toast {
        pointer-events: auto;
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        padding: var(--space-4);
        background: var(--color-surface-card);
        border: 1px solid var(--color-border);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-overlay);
        animation: toast-in var(--duration-base) var(--ease-enter) backwards;
      }

      .toast--leaving {
        animation: toast-out var(--duration-fast) var(--ease-exit) forwards;
      }

      .toast__icon {
        flex-shrink: 0;
        margin-top: 1px;
      }
      .toast--success .toast__icon {
        color: var(--color-success-text);
      }
      .toast--info .toast__icon {
        color: var(--color-info);
      }
      .toast--warning .toast__icon {
        color: var(--color-warning-text-strong);
      }
      .toast--error .toast__icon {
        color: var(--color-danger-text);
      }

      .toast__message {
        flex: 1;
        min-width: 0;
        margin: 0;
        font-size: var(--font-size-md);
        line-height: 1.4;
        color: var(--color-text-primary);
        overflow-wrap: break-word;
      }

      .toast__action {
        flex-shrink: 0;
        padding: var(--space-2) var(--space-4);
        border: 1px solid var(--color-border-strong);
        border-radius: var(--radius-sm);
        background: transparent;
        // Bug real de contraste en dark mode (Judgment Day): --color-brand como TEXTO cae bajo
        // 4.5:1 sobre --color-surface-card oscuro — --color-link sí tiene su propia variante dark.
        color: var(--color-link);
        font-family: var(--font-body);
        font-size: var(--font-size-sm);
        font-weight: 600;
        cursor: pointer;
        transition:
          background-color var(--duration-fast) var(--ease-standard),
          border-color var(--duration-fast) var(--ease-standard);
      }
      .toast__action:hover {
        background: var(--color-surface-alt);
      }
      .toast__action:active {
        background: var(--color-surface-alt);
        border-color: var(--color-link);
      }
      .toast__action:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .toast__close {
        flex-shrink: 0;
        display: inline-flex;
        padding: var(--space-1);
        border: none;
        background: none;
        color: var(--color-text-tertiary);
        cursor: pointer;
      }
      .toast__close:hover,
      .toast__close:active {
        color: var(--color-text-primary);
      }
      .toast__close:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .toast__action:focus-visible,
      .toast__close:focus-visible {
        outline: 2px solid var(--color-focus-ring);
        outline-offset: 2px;
        border-radius: var(--radius-sm);
      }

      @keyframes toast-in {
        from {
          opacity: 0;
          transform: translateY(12px);
        }
        to {
          opacity: 1;
          transform: none;
        }
      }

      @keyframes toast-out {
        to {
          opacity: 0;
          transform: translateY(8px);
        }
      }

      @media (max-width: 768px) {
        .toast-container {
          right: var(--space-4);
          left: var(--space-4);
          bottom: var(--space-4);
          width: auto;
        }
      }
    `,
  ],
})
export class ToastContainer {
  private readonly service = inject(ToastService);

  protected readonly toasts = this.service.toasts;

  protected iconFor(kind: ToastKind): IconName {
    return KIND_ICONS[kind];
  }

  protected roleFor(kind: ToastKind): 'status' | 'alert' {
    return KIND_ROLES[kind];
  }

  protected dismiss(id: number): void {
    this.service.dismiss(id);
  }

  protected runAction(toast: Toast): void {
    toast.action?.run();
    this.service.dismiss(toast.id);
  }
}
